/**
 * Estado de una partida: rejilla, anotaciones a lápiz, pistas tachadas e historial.
 *
 * **Ejes de la rejilla: `grid[hora][sala] = persona`.** Columnas = salas, filas = horas,
 * y en la casilla va quien estuvo allí. Es el conjugado del cuadrado latino que emite el
 * motor (`solution[persona][hora] = sala`) y las dos reglas del MVP §1 se conservan
 * intactas, solo cambian de nombre:
 *
 * | Regla del MVP                             | En estos ejes                          |
 * | ----------------------------------------- | -------------------------------------- |
 * | una persona por sala y hora               | en cada **fila**, cada persona una vez |
 * | cada persona pasa por cada sala una vez   | en cada **columna**, cada persona una vez |
 *
 * Se pinta así porque la celda del crimen es entonces **una sola casilla** (sala y hora
 * del cuerpo), y quien escribas en ella es tu acusación. El motor no cambia: `gridOf`
 * traduce su solución a estos ejes.
 *
 * Todo lo que el jugador construye vive en `progress`, que es **serializable tal cual**
 * (`JSON.stringify`): la tarea de progreso local (MON-61) puede guardarlo y devolverlo
 * con `restoreCaseState` sin refactorizar nada de aquí.
 *
 * `counts` es la única estructura derivada y se mantiene **de forma incremental**: cada
 * jugada toca dos contadores, y saber si una celda está en conflicto es O(1) al pintar.
 * Así los conflictos aparecen y desaparecen sin recorrer la rejilla (MVP §6.2). El
 * invariante «counts siempre coincide con recontar desde cero» está cubierto por tests.
 */

import type { Grid } from '@murdoku/engine';

export const PROGRESS_VERSION = 2;

/** Persona colocada en una celda, o `null` si está vacía. */
export type Cell = number | null;

export interface CaseProgress {
  version: number;
  /** Orden del caso: personas = salas = horas = N. */
  n: number;
  /** `grid[hora][sala] = personaId | null`. */
  grid: Cell[][];
  /** `notes[hora][sala]` = personas descartadas en esa casilla, ordenadas. */
  notes: number[][][];
  /** `id` de las pistas que el jugador ha tachado. */
  struck: string[];
  /**
   * Jugadas que crearon un conflicto, acumuladas. Es monótono a propósito: deshacer no
   * borra el error cometido, y es el número que se enseñará en la tarjeta compartible
   * (MON-60).
   */
  errors: number;
}

/** Contadores derivados de la rejilla. `byHourPerson[h][p]`, `byRoomPerson[r][p]`. */
export interface Counts {
  byHourPerson: number[][];
  byRoomPerson: number[][];
}

/** Lo que deshace/rehace el historial: la rejilla y las anotaciones. */
interface Snapshot {
  grid: Cell[][];
  notes: number[][][];
}

export interface CaseState {
  progress: CaseProgress;
  counts: Counts;
  past: Snapshot[];
  future: Snapshot[];
}

/** Tope del historial. 200 jugadas cubren de sobra un caso N=6 de 36 celdas. */
const HISTORY_LIMIT = 200;

export type CaseAction =
  | { type: 'place'; hour: number; room: number; person: number }
  | { type: 'clearCell'; hour: number; room: number }
  | { type: 'toggleNote'; hour: number; room: number; person: number }
  | { type: 'toggleClue'; id: string }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'clearGrid' }
  | { type: 'restore'; progress: CaseProgress };

function rowOf<T>(rows: readonly T[][], index: number, what: string): T[] {
  const row = rows[index];
  if (row === undefined) throw new RangeError(`${what} fuera de rango: ${index}`);
  return row;
}

function square<T>(n: number, value: () => T): T[][] {
  return Array.from({ length: n }, () => Array.from({ length: n }, value));
}

/** Sustituye una fila devolviendo una matriz nueva y compartiendo el resto. */
function withRow<T>(rows: T[][], index: number, next: T[]): T[][] {
  return rows.map((row, i) => (i === index ? next : row));
}

/**
 * Traduce la solución del motor (`solution[persona][hora] = sala`) a los ejes de la
 * rejilla (`grid[hora][sala] = persona`). Lo usan la vista de solución y los tests.
 */
export function gridOf(solution: Grid): number[][] {
  const n = solution.length;
  const grid = square(n, () => -1);
  for (let person = 0; person < n; person += 1) {
    const row = rowOf(solution, person, 'persona');
    for (let hour = 0; hour < n; hour += 1) {
      const room = row[hour];
      if (room === undefined) throw new RangeError(`hora fuera de rango: ${hour}`);
      rowOf(grid, hour, 'hora')[room] = person;
    }
  }
  return grid;
}

export function emptyProgress(n: number): CaseProgress {
  if (!Number.isInteger(n) || n < 2) throw new RangeError(`N inválido: ${n}`);
  return {
    version: PROGRESS_VERSION,
    n,
    grid: square<Cell>(n, () => null),
    notes: square<number[]>(n, () => []),
    struck: [],
    errors: 0,
  };
}

/** Reconteo desde cero. Solo se usa al arrancar, al restaurar y al deshacer. */
export function countsOf(grid: readonly Cell[][], n: number): Counts {
  const counts: Counts = {
    byHourPerson: square(n, () => 0),
    byRoomPerson: square(n, () => 0),
  };
  for (let hour = 0; hour < n; hour += 1) {
    const row = rowOf(grid, hour, 'hora');
    const hourCounts = rowOf(counts.byHourPerson, hour, 'hora');
    for (let room = 0; room < n; room += 1) {
      const person = row[room];
      if (person === undefined || person === null) continue;
      const roomCounts = rowOf(counts.byRoomPerson, room, 'sala');
      hourCounts[person] = (hourCounts[person] ?? 0) + 1;
      roomCounts[person] = (roomCounts[person] ?? 0) + 1;
    }
  }
  return counts;
}

export function createCaseState(n: number): CaseState {
  const progress = emptyProgress(n);
  return { progress, counts: countsOf(progress.grid, n), past: [], future: [] };
}

/** Rehidrata un estado desde un `CaseProgress` guardado (MON-61). */
export function restoreCaseState(progress: CaseProgress): CaseState {
  return {
    progress,
    counts: countsOf(progress.grid, progress.n),
    past: [],
    future: [],
  };
}

function countAt(rows: readonly number[][], index: number, person: number): number {
  return rows[index]?.[person] ?? 0;
}

/** Contadores con `delta` aplicado a (hora, persona) y (sala, persona). */
function bump(
  counts: Counts,
  hour: number,
  room: number,
  person: number,
  delta: number,
): Counts {
  const hourRow = [...rowOf(counts.byHourPerson, hour, 'hora')];
  hourRow[person] = (hourRow[person] ?? 0) + delta;
  const roomRow = [...rowOf(counts.byRoomPerson, room, 'sala')];
  roomRow[person] = (roomRow[person] ?? 0) + delta;
  return {
    byHourPerson: withRow(counts.byHourPerson, hour, hourRow),
    byRoomPerson: withRow(counts.byRoomPerson, room, roomRow),
  };
}

/**
 * Una celda ocupada está en conflicto si la persona se repite en su fila (estaría en
 * dos salas a la vez) o en su columna (volvería a una sala). Ver MVP §1.
 */
function conflictsAt(counts: Counts, hour: number, room: number, person: number) {
  return {
    row: countAt(counts.byHourPerson, hour, person) > 1,
    column: countAt(counts.byRoomPerson, room, person) > 1,
  };
}

function snapshotOf(progress: CaseProgress): Snapshot {
  return { grid: progress.grid, notes: progress.notes };
}

function pushHistory(past: Snapshot[], snapshot: Snapshot): Snapshot[] {
  const next = [...past, snapshot];
  return next.length > HISTORY_LIMIT ? next.slice(next.length - HISTORY_LIMIT) : next;
}

export function caseReducer(state: CaseState, action: CaseAction): CaseState {
  const { progress } = state;
  const { n } = progress;

  switch (action.type) {
    case 'place': {
      const { hour, room, person } = action;
      const previous = cellPerson(state, hour, room);
      if (previous === person) return state;

      let counts = state.counts;
      if (previous !== null) counts = bump(counts, hour, room, previous, -1);
      counts = bump(counts, hour, room, person, +1);

      const row = [...rowOf(progress.grid, hour, 'hora')];
      row[room] = person;
      const conflict = conflictsAt(counts, hour, room, person);

      return {
        progress: {
          ...progress,
          grid: withRow(progress.grid, hour, row),
          errors: progress.errors + (conflict.row || conflict.column ? 1 : 0),
        },
        counts,
        past: pushHistory(state.past, snapshotOf(progress)),
        future: [],
      };
    }

    case 'clearCell': {
      const { hour, room } = action;
      const previous = cellPerson(state, hour, room);
      if (previous === null) return state;

      const row = [...rowOf(progress.grid, hour, 'hora')];
      row[room] = null;

      return {
        progress: { ...progress, grid: withRow(progress.grid, hour, row) },
        counts: bump(state.counts, hour, room, previous, -1),
        past: pushHistory(state.past, snapshotOf(progress)),
        future: [],
      };
    }

    case 'toggleNote': {
      const { hour, room, person } = action;
      const hourNotes = rowOf(progress.notes, hour, 'hora');
      const current = hourNotes[room] ?? [];
      const next = current.includes(person)
        ? current.filter((p) => p !== person)
        : [...current, person].sort((a, b) => a - b);
      const nextRow = [...hourNotes];
      nextRow[room] = next;

      return {
        ...state,
        progress: { ...progress, notes: withRow(progress.notes, hour, nextRow) },
        past: pushHistory(state.past, snapshotOf(progress)),
        future: [],
      };
    }

    case 'toggleClue': {
      const struck = progress.struck.includes(action.id)
        ? progress.struck.filter((id) => id !== action.id)
        : [...progress.struck, action.id];
      return { ...state, progress: { ...progress, struck } };
    }

    case 'clearGrid': {
      const empty = emptyProgress(n);
      return {
        progress: { ...progress, grid: empty.grid, notes: empty.notes },
        counts: countsOf(empty.grid, n),
        past: pushHistory(state.past, snapshotOf(progress)),
        future: [],
      };
    }

    case 'undo': {
      const previous = state.past[state.past.length - 1];
      if (previous === undefined) return state;
      return {
        progress: { ...progress, grid: previous.grid, notes: previous.notes },
        counts: countsOf(previous.grid, n),
        past: state.past.slice(0, -1),
        future: [snapshotOf(progress), ...state.future],
      };
    }

    case 'redo': {
      const [next, ...rest] = state.future;
      if (next === undefined) return state;
      return {
        progress: { ...progress, grid: next.grid, notes: next.notes },
        counts: countsOf(next.grid, n),
        past: pushHistory(state.past, snapshotOf(progress)),
        future: rest,
      };
    }

    case 'restore':
      return restoreCaseState(action.progress);
  }
}

/* ------------------------------ selectores ------------------------------ */

export function cellPerson(state: CaseState, hour: number, room: number): Cell {
  return rowOf(state.progress.grid, hour, 'hora')[room] ?? null;
}

export function cellNotes(state: CaseState, hour: number, room: number): number[] {
  return rowOf(state.progress.notes, hour, 'hora')[room] ?? [];
}

export interface CellConflict {
  /** La persona estaría en dos salas a la vez. */
  row: boolean;
  /** La persona volvería a una sala por la que ya pasó. */
  column: boolean;
}

/** O(1): no recorre la rejilla. `null` si la celda está vacía. */
export function cellConflict(
  state: CaseState,
  hour: number,
  room: number,
): CellConflict | null {
  const person = cellPerson(state, hour, room);
  if (person === null) return null;
  const conflict = conflictsAt(state.counts, hour, room, person);
  return conflict.row || conflict.column ? conflict : null;
}

export function filledCount(state: CaseState): number {
  return state.progress.grid.reduce(
    (total, row) => total + row.filter((cell) => cell !== null).length,
    0,
  );
}

export function conflictCount(state: CaseState): number {
  const { n } = state.progress;
  let total = 0;
  for (let hour = 0; hour < n; hour += 1) {
    for (let room = 0; room < n; room += 1) {
      if (cellConflict(state, hour, room) !== null) total += 1;
    }
  }
  return total;
}

/** Rejilla completa y sin conflictos: es un cuadrado latino (aún sin acusar). */
export function isComplete(state: CaseState): boolean {
  const { n } = state.progress;
  return filledCount(state) === n * n && conflictCount(state) === 0;
}

export function isStruck(state: CaseState, clueId: string): boolean {
  return state.progress.struck.includes(clueId);
}

export function canUndo(state: CaseState): boolean {
  return state.past.length > 0;
}

export function canRedo(state: CaseState): boolean {
  return state.future.length > 0;
}

/* ---------------------------- serialización ---------------------------- */

/**
 * Valida un `CaseProgress` que viene de fuera (localStorage, URL…). Devuelve `null`
 * en vez de lanzar: un guardado corrupto —o de la versión anterior, con los ejes al
 * revés— no debe romper la partida (MON-61).
 */
export function parseProgress(raw: unknown, n: number): CaseProgress | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const value = raw as Partial<CaseProgress>;
  if (value.version !== PROGRESS_VERSION || value.n !== n) return null;

  const isSquareOf = (rows: unknown, ok: (cell: unknown) => boolean): boolean =>
    Array.isArray(rows) &&
    rows.length === n &&
    rows.every((row) => Array.isArray(row) && row.length === n && row.every(ok));

  const isPerson = (cell: unknown): boolean =>
    Number.isInteger(cell) && (cell as number) >= 0 && (cell as number) < n;

  if (!isSquareOf(value.grid, (cell) => cell === null || isPerson(cell))) return null;
  if (!isSquareOf(value.notes, (cell) => Array.isArray(cell) && cell.every(isPerson))) {
    return null;
  }
  if (
    !Array.isArray(value.struck) ||
    !value.struck.every((id) => typeof id === 'string')
  )
    return null;
  if (!Number.isInteger(value.errors) || (value.errors ?? -1) < 0) return null;

  return value as CaseProgress;
}
